/** Segments URL : /minigrid/:id/live/:energySegment */
export type LiveEnergySegment = 'solar' | 'battery' | 'consumption' | 'generator'

export const LIVE_ENERGY_SEGMENTS: LiveEnergySegment[] = [
  'solar',
  'battery',
  'consumption',
  'generator',
]

export function isLiveEnergySegment(s: string | undefined): s is LiveEnergySegment {
  return LIVE_ENERGY_SEGMENTS.includes(s as LiveEnergySegment)
}

/** Une courbe dans un graphique multi-séries (ex. L1 / L2 / L3). */
export type MomiChartLine = {
  measurement: string
  label: string
}

export type MomiChartSpec = {
  title: string
  unit: string
  measurement: string
  label: string
  /** ≥ 2 entrées : un seul ECharts avec une courbe par mesure (ex. phases 1–3). */
  multiLine?: MomiChartLine[]
  /** Facteur multiplicatif appliqué aux valeurs affichées (ex. 0.001 pour W→kW). */
  scale?: number
}

/** Clé stable pour React (grille + modale). */
export function momiChartSpecKey(spec: MomiChartSpec): string {
  if (spec.multiLine?.length) {
    return spec.multiLine.map((l) => l.measurement).join('|')
  }
  return spec.measurement
}

export function isMomiMultiLineSpec(spec: MomiChartSpec): boolean {
  return (spec.multiLine?.length ?? 0) >= 2
}

const SOLAR: MomiChartSpec[] = [
  {
    measurement: 'ac_pv_output_power',
    title: 'Puissance PV AC (sortie onduleur)',
    label: 'AC PV sortie',
    unit: 'kW',
    scale: 0.001,
  },
  {
    measurement: 'dc_pv_power',
    title: 'Puissance PV DC (panneaux)',
    label: 'DC PV',
    unit: 'kW',
    scale: 0.001,
  },
]

const BATTERY: MomiChartSpec[] = [
  {
    measurement: 'battery_soc',
    title: 'SOC batterie',
    label: 'État de charge',
    unit: '%',
  },
  {
    measurement: 'dc_battery_power',
    title: 'Puissance batterie',
    label: 'Puissance DC batterie',
    unit: 'kW',
    scale: 0.001,
  },
  {
    measurement: 'dc_battery_voltage',
    title: 'Tension batterie',
    label: 'Tension DC batterie',
    unit: 'V',
  },
]

/** Compteur Schneider (sortant = consommation). _measurement côté Influx, tag uuid MoMi. Pas de séries CCGX. */
const CONSUMPTION: MomiChartSpec[] = [
  {
    measurement: 'active_power_total',
    title: 'Puissance active totale',
    label: 'P active totale',
    unit: 'kW',
  },
  {
    measurement: 'reactive_power_total',
    title: 'Puissance réactive totale',
    label: 'Q réactive totale',
    unit: 'kVAR',
  },
  {
    measurement: 'apparent_power_total',
    title: 'Puissance apparente totale',
    label: 'S apparente totale',
    unit: 'kVA',
  },
  {
    measurement: 'active_power_phase_1',
    title: 'Puissance active par phase',
    label: 'P active',
    unit: 'kW',
    multiLine: [
      { measurement: 'active_power_phase_1', label: 'L1' },
      { measurement: 'active_power_phase_2', label: 'L2' },
      { measurement: 'active_power_phase_3', label: 'L3' },
    ],
  },
  {
    measurement: 'active_energy_phase_1_import',
    title: 'Énergie active importée par phase',
    label: 'Énergie import',
    unit: 'kWh',
    multiLine: [
      { measurement: 'active_energy_phase_1_import', label: 'L1' },
      { measurement: 'active_energy_phase_2_import', label: 'L2' },
      { measurement: 'active_energy_phase_3_import', label: 'L3' },
    ],
  },
  {
    measurement: 'active_total_energy_import',
    title: 'Énergie active totale importée',
    label: 'E active import',
    unit: 'kWh',
  },
  {
    measurement: 'active_total_energy_export',
    title: 'Énergie active totale exportée',
    label: 'E active export',
    unit: 'kWh',
  },
  {
    measurement: 'reactive_total_energy_import',
    title: 'Énergie réactive totale importée',
    label: 'E réactive import',
    unit: 'kVARh',
  },
  {
    measurement: 'reactive_total_energy_export',
    title: 'Énergie réactive totale exportée',
    label: 'E réactive export',
    unit: 'kVARh',
  },
  {
    measurement: 'voltage_l_n_avg',
    title: 'Tension L-N (moyenne)',
    label: 'Tension L-N moy.',
    unit: 'V',
  },
  {
    measurement: 'voltage_l1_n',
    title: 'Tension L-N par phase',
    label: 'Tension L-N',
    unit: 'V',
    multiLine: [
      { measurement: 'voltage_l1_n', label: 'L1' },
      { measurement: 'voltage_l2_n', label: 'L2' },
      { measurement: 'voltage_l3_n', label: 'L3' },
    ],
  },
  {
    measurement: 'freq',
    title: 'Fréquence',
    label: 'Fréquence',
    unit: 'Hz',
  },
]

const GENERATOR: MomiChartSpec[] = [
  {
    measurement: 'genset_power',
    title: 'Puissance groupe',
    label: 'Genset',
    unit: 'W',
  },
  {
    measurement: 'ac_pv_genset_power',
    title: 'PV / genset (AC)',
    label: 'AC PV genset',
    unit: 'W',
  },
  {
    measurement: 'ac_input_power',
    title: 'Puissance entrée AC',
    label: 'AC input',
    unit: 'W',
  },
]

const SEGMENT_CHARTS: Record<LiveEnergySegment, MomiChartSpec[]> = {
  solar: SOLAR,
  battery: BATTERY,
  consumption: CONSUMPTION,
  generator: GENERATOR,
}

const SEGMENT_TITLES: Record<LiveEnergySegment, string> = {
  solar: 'Solaire',
  battery: 'Batterie',
  consumption: 'Consommation',
  generator: 'Groupe électrogène',
}

export function chartsForSegment(segment: LiveEnergySegment): MomiChartSpec[] {
  return SEGMENT_CHARTS[segment]
}

export function pageTitleForSegment(segment: LiveEnergySegment): string {
  return SEGMENT_TITLES[segment]
}
